// src/pages/Users/Vote/ParticipateVoteModal.jsx
import React, { useEffect, useState } from "react";
import { getVoteById, submitVote } from "../../../services/voteServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import { FaTimes, FaPaperPlane } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css"; // thème léger pour readOnly

const ParticipateVoteModal = ({
  isOpen,
  onClose,
  voteId,
  userId,
  refreshVotes,
}) => {
  const [vote, setVote] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && voteId) {
      (async () => {
        try {
          const data = await getVoteById(voteId);
          setVote(data);
        } catch {
          toast.error("Erreur lors du chargement du vote");
        }
      })();
    } else {
      setVote(null);
      setResponses({});
      setSubmitting(false);
    }
  }, [isOpen, voteId]);

  const handleChange = (questionId, value, type) => {
    if (type === "multiple") {
      setResponses((prev) => {
        const prevChoices = prev[questionId] || [];
        return {
          ...prev,
          [questionId]: prevChoices.includes(value)
            ? prevChoices.filter((v) => v !== value)
            : [...prevChoices, value],
        };
      });
    } else {
      setResponses((prev) => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!voteId || !userId) return;
    const payload = { vote_id: voteId, user_id: userId, responses };

    try {
      setSubmitting(true);
      const ok = await submitVote(payload);
      if (!ok) throw new Error("Échec de la soumission");
      toast.success("Réponses enregistrées !");
      onClose?.();
      refreshVotes?.();
    } catch (e) {
      console.error("❌ Erreur soumission vote :", e);
      toast.error("Erreur lors de l'envoi des réponses");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !vote) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-[101] w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900">
        {/* Bandeau gradient */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 sm:px-8 py-4 text-white">
          <h3 className="text-lg sm:text-xl font-semibold text-center">
            {vote.title}
          </h3>
          {!!vote.description && (
            <p className="text-sm/6 opacity-95 text-center mt-1">
              {vote.description}
            </p>
          )}
        </div>

        {/* Contenu scrollable */}
        <div className="p-5 sm:p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {(vote.questions || []).map((q) => (
              <div
                key={q.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-4"
              >
                {/* Énoncé (rich text en readOnly) */}
                <div className="mb-3 prose prose-sm max-w-none dark:prose-invert">
                  <ReactQuill value={q.question_text} readOnly theme="bubble" />
                </div>

                {/* Réponses selon type */}
                {q.type === "open" && (
                  <textarea
                    rows={3}
                    placeholder="Votre réponse"
                    value={responses[q.id] || ""}
                    onChange={(e) => handleChange(q.id, e.target.value, q.type)}
                    className="textarea textarea-bordered w-full bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                )}

                {q.type === "single" && (
                  <div className="space-y-2">
                    {(q.choices || []).map((choice) => (
                      <label
                        key={choice.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name={`question_${q.id}`}
                          value={choice.id}
                          checked={responses[q.id] === choice.id}
                          onChange={() =>
                            handleChange(q.id, choice.id, q.type)
                          }
                          className="radio"
                        />
                        <span>{choice.choice_text}</span>
                      </label>
                    ))}
                  </div>
                )}

                {q.type === "multiple" && (
                  <div className="space-y-2">
                    {(q.choices || []).map((choice) => {
                      const checked =
                        Array.isArray(responses[q.id]) &&
                        responses[q.id].includes(choice.id);
                      return (
                        <label
                          key={choice.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            value={choice.id}
                            checked={!!checked}
                            onChange={() =>
                              handleChange(q.id, choice.id, q.type)
                            }
                            className="checkbox"
                          />
                          <span>{choice.choice_text}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-center gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              className="gap-2 !rounded-xl"
              disabled={submitting}
            />
            <Button
              text={submitting ? "Envoi..." : "Envoyer"}
              icon={FaPaperPlane}
              onClick={handleSubmit}
              className="gap-2 !rounded-xl"
              disabled={submitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipateVoteModal;
